import constantCreator from 'utils/constantCreator';
import api from 'utils/api';
import moment from 'moment-timezone';

moment.tz.setDefault('Asia/Bangkok');

const define = constantCreator('walkingTour');

// Constants for async actions (Bookings)
const GET_BOOKINGS = define('GET_BOOKINGS', true);
const GET_BOOKING_DETAIL = define('GET_BOOKING_DETAIL', true);
const APPROVE_REFUND = define('APPROVE_REFUND', true);
const REJECT_REFUND = define('REJECT_REFUND', true);
const GET_AVAILABILITY = define('GET_AVAILABILITY', true);
const GET_PRODUCT = define('GET_PRODUCT', true);
const POST_AVAILABILITY = define('POST_AVAILABILITY', true);

// Constants for async actions (CMS Products)
const GET_PRODUCTS_ADMIN = define('GET_PRODUCTS_ADMIN', true);
const GET_PRODUCT_ADMIN = define('GET_PRODUCT_ADMIN', true);
const CREATE_PRODUCT = define('CREATE_PRODUCT', true);
const UPDATE_PRODUCT = define('UPDATE_PRODUCT', true);
const APPROVE_PRODUCT = define('APPROVE_PRODUCT', true);

// Constants for filter actions
const SET_FILTERS = define('SET_FILTERS');
const CLEAR_FILTERS = define('CLEAR_FILTERS');
const SET_SEARCH_TERM = define('SET_SEARCH_TERM');

// Constants for CMS actions
const CLEAR_CURRENT_PRODUCT = define('CLEAR_CURRENT_PRODUCT');

// Initial state
const initialState = {
  isLoading: false,
  isLoadingDetail: false,
  processing: false,
  bookings: {
    data: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },
    filters: {
      fromDate: moment().subtract(7, 'days').startOf('day'),
      toDate: moment().add(7, 'days').endOf('day'),
      status: undefined,
      search_term: ''
    },
    sorting: {
      sortBy: '',
      sortOrder: ''
    }
  },
  currentBooking: null,
  unavailable_dates: [],
  products: [],
  error: null,
  postAvailabilityResult: null,
  isPostingAvailability: false,
  // CMS Products State
  cmsProducts: {
    data: [],
    isLoading: false,
    error: null
  },
  currentProduct: null,
  isLoadingProduct: false,
  isSaving: false,
  saveError: null
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case GET_BOOKINGS.PENDING:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case GET_BOOKINGS.RESOLVED:
      return {
        ...state,
        isLoading: false,
        bookings: {
          ...state.bookings,
          data: action.data.bookings || [],
          pagination: {
            ...state.bookings.pagination,
            ...action.data.pagination
          }
        }
      };
    case GET_BOOKINGS.REJECTED:
      return {
        ...state,
        isLoading: false,
        error: action.error
      };

    case SET_FILTERS:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          filters: {
            ...state.bookings.filters,
            ...action.filters
          }
        }
      };

    case CLEAR_FILTERS:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          filters: {
            ...initialState.bookings.filters,
          }
        }
      };

    case SET_SEARCH_TERM:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          filters: {
            ...state.bookings.filters,
            search_term: action.searchTerm
          }
        }
      };
    case GET_AVAILABILITY.PENDING:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case GET_AVAILABILITY.RESOLVED:
      return {
        ...state,
        isLoading: false,
        unavailable_dates: action.data.unavailable_dates || [],
      };
    case GET_AVAILABILITY.REJECTED:
      return {
        ...state,
        isLoading: false,
        error: action.error
      };
    case GET_PRODUCT.PENDING:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case GET_PRODUCT.RESOLVED:
      return {
        ...state,
        isLoading: false,
        products: {
          ...state.products,
          data: action.data.products || [],
        }
      };
    case GET_PRODUCT.REJECTED:
      return {
        ...state,
        isLoading: false,
        error: action.error
      };

    case GET_BOOKING_DETAIL.PENDING:
      return {
        ...state,
        isLoadingDetail: true,
        error: null
      };
    case GET_BOOKING_DETAIL.RESOLVED:
      return {
        ...state,
        isLoadingDetail: false,
        currentBooking: action.data.booking
      };
    case GET_BOOKING_DETAIL.REJECTED:
      return {
        ...state,
        isLoadingDetail: false,
        error: action.error
      };

    case APPROVE_REFUND.PENDING:
    case REJECT_REFUND.PENDING:
      return {
        ...state,
        processing: true,
        error: null
      };

    case APPROVE_REFUND.RESOLVED:
    case REJECT_REFUND.RESOLVED:
      return {
        ...state,
        processing: false,
        // Update the current booking if it exists
        currentBooking: state.currentBooking && state.currentBooking.id === action.data.id
          ? action.data
          : state.currentBooking
      };

    case APPROVE_REFUND.REJECTED:
    case REJECT_REFUND.REJECTED:
      return {
        ...state,
        processing: false,
        error: action.error
      };

    case POST_AVAILABILITY.PENDING:
      return {
        ...state,
        processing: true,
        postAvailabilityResult: null,
        error: null
      };
    case POST_AVAILABILITY.RESOLVED:
      return {
        ...state,
        processing: false,
        postAvailabilityResult: action.data,
      };
    case POST_AVAILABILITY.REJECTED:
      return {
        ...state,
        processing: false,
        postAvailabilityResult: null,
        error: action.error
      };

    // CMS Products - Get All Products
    case GET_PRODUCTS_ADMIN.PENDING:
      return {
        ...state,
        cmsProducts: {
          ...state.cmsProducts,
          isLoading: true,
          error: null
        }
      };
    case GET_PRODUCTS_ADMIN.RESOLVED:
      return {
        ...state,
        cmsProducts: {
          data: action.data.products || [],
          isLoading: false,
          error: null
        }
      };
    case GET_PRODUCTS_ADMIN.REJECTED:
      return {
        ...state,
        cmsProducts: {
          ...state.cmsProducts,
          isLoading: false,
          error: action.error
        }
      };

    // CMS Products - Get Single Product
    case GET_PRODUCT_ADMIN.PENDING:
      return {
        ...state,
        isLoadingProduct: true,
        currentProduct: null,
        saveError: null
      };
    case GET_PRODUCT_ADMIN.RESOLVED:
      return {
        ...state,
        isLoadingProduct: false,
        currentProduct: action.data.product
      };
    case GET_PRODUCT_ADMIN.REJECTED:
      return {
        ...state,
        isLoadingProduct: false,
        saveError: action.error
      };

    // CMS Products - Create Product
    case CREATE_PRODUCT.PENDING:
      return {
        ...state,
        isSaving: true,
        saveError: null
      };
    case CREATE_PRODUCT.RESOLVED:
      return {
        ...state,
        isSaving: false,
        currentProduct: action.data.product,
        cmsProducts: {
          ...state.cmsProducts,
          data: [action.data.product, ...state.cmsProducts.data]
        }
      };
    case CREATE_PRODUCT.REJECTED:
      return {
        ...state,
        isSaving: false,
        saveError: action.error
      };

    // CMS Products - Update Product
    case UPDATE_PRODUCT.PENDING:
      return {
        ...state,
        isSaving: true,
        saveError: null
      };
    case UPDATE_PRODUCT.RESOLVED:
      return {
        ...state,
        isSaving: false,
        currentProduct: action.data.product,
        cmsProducts: {
          ...state.cmsProducts,
          data: state.cmsProducts.data.map(function(p) {
            return p._id === action.data.product._id ? action.data.product : p;
          })
        }
      };
    case UPDATE_PRODUCT.REJECTED:
      return {
        ...state,
        isSaving: false,
        saveError: action.error
      };

    // CMS Products - Approve Product
    case APPROVE_PRODUCT.PENDING:
      return {
        ...state,
        isSaving: true,
        saveError: null
      };
    case APPROVE_PRODUCT.RESOLVED:
      return {
        ...state,
        isSaving: false,
        currentProduct: action.data.product,
        cmsProducts: {
          ...state.cmsProducts,
          data: state.cmsProducts.data.map(function(p) {
            return p._id === action.data.product._id ? action.data.product : p;
          })
        }
      };
    case APPROVE_PRODUCT.REJECTED:
      return {
        ...state,
        isSaving: false,
        saveError: action.error
      };

    // CMS Products - Clear Current Product
    case CLEAR_CURRENT_PRODUCT:
      return {
        ...state,
        currentProduct: null,
        saveError: null
      };

    default:
      return state;
  }
};

export const actions = {
  getBookings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return {
      type: GET_BOOKINGS,
      promise: api.get(`/walking-tour/bookings/admin${query ? `?${query}` : ''}`)
    };
  },
  getBookingDetail: (bookingId) => ({
    type: GET_BOOKING_DETAIL,
    promise: api.get(`/walking-tour/bookings/admin/${bookingId}`)
  }),
  approveRefund: (bookingId) => ({
    type: APPROVE_REFUND,
    promise: api.post(`/walking-tour/bookings/admin/refund/${bookingId}`, {
      action: 'approve'
    })
  }),
  rejectRefund: (bookingId, reason) => ({
    type: REJECT_REFUND,
    promise: api.post(`/walking-tour/bookings/admin/refund/${bookingId}`, {
      action: 'reject',
      reason
    })
  }),
  getAvailability: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return {
      type: GET_AVAILABILITY,
      promise: api.get(`/walking-tour/bookings/availability${query ? `?${query}` : ''}`)
    };
  },
  getProducts: () => ({
    type: GET_PRODUCT,
    promise: api.get('/walking-tour/products')
  }),
  postAvailability: (payload) => ({
    type: POST_AVAILABILITY,
    promise: api.post('/walking-tour/bookings/availability', payload)
  }),
  setFilters: (filters) => ({
    type: SET_FILTERS,
    filters
  }),
  clearFilters: () => ({
    type: CLEAR_FILTERS
  }),
  setSearchTerm: (searchTerm) => ({
    type: SET_SEARCH_TERM,
    searchTerm
  }),
  applyFiltersAndFetch: (filters, updateURL = null) => (dispatch) => {
    // Set filters in state
    dispatch(actions.setFilters(filters));

    // Prepare API parameters
    const params = {
      limit: 9999,
      from_date: filters.fromDate.clone().startOf('day').toISOString(),
      to_date: filters.toDate.clone().endOf('day').toISOString(),
    };
    if (filters.status) params.status = filters.status;
    if (filters.search_term) params.reference_number = filters.search_term;

    // Update URL if updateURL function is provided
    if (updateURL && typeof updateURL === 'function') {
      updateURL(params);
    }

    // Fetch bookings with filters
    return dispatch(actions.getBookings(params));
  },
  clearAllFiltersAndFetch: (updateURL = null) => (dispatch) => {
    // Clear filters in state
    dispatch(actions.clearFilters());

    // Clear URL if updateURL function is provided
    if (updateURL && typeof updateURL === 'function') {
      updateURL({});
    }

    // Fetch bookings without filters
    return dispatch(actions.getBookings({ limit: 9999 }));
  },
  // CMS Product Actions
  getProductsAdmin: () => ({
    type: GET_PRODUCTS_ADMIN,
    promise: api.get('/walking-tour/products/admin')
  }),
  getProductAdmin: (id) => ({
    type: GET_PRODUCT_ADMIN,
    promise: api.get(`/walking-tour/products/admin/${id}`)
  }),
  createProduct: (data) => ({
    type: CREATE_PRODUCT,
    promise: api.post('/walking-tour/products/admin', data)
  }),
  updateProduct: (id, data) => ({
    type: UPDATE_PRODUCT,
    promise: api.put(`/walking-tour/products/admin/${id}`, data)
  }),
  approveProduct: (id, status) => ({
    type: APPROVE_PRODUCT,
    promise: api.put(`/walking-tour/products/admin/${id}/approval`, { status })
  }),
  clearCurrentProduct: () => ({
    type: CLEAR_CURRENT_PRODUCT
  })
};



// WEBPACK FOOTER //
// ./src/ducks/walking-tour.js